export interface PasswordPromptConfig {
    prelude?: string[];
    prompt: string;
    maxAttempts: number;
    retryLine: string;
    failureLines: string[];
}

export type CommandBlock =
    | { type: "lines"; lines: string[] }
    | { type: "password"; config: PasswordPromptConfig };

const SENSITIVE_PATHS = [
    "/etc/shadow",
    "/etc/sudoers",
    "/etc/passwd",
    "/root",
    "/boot",
    "/sys",
    "/proc/kcore",
] as const;

const NOT_FOUND = [
    "nmap",
    "msfconsole",
    "metasploit",
    "sqlmap",
    "hydra",
    "john",
    "hashcat",
    "aircrack-ng",
    "wireshark",
    "nikto",
    "burpsuite",
    "beef",
    "setoolkit",
] as const;

const ROOT_ONLY = [
    "apt",
    "apt-get",
    "yum",
    "dnf",
    "pacman",
    "systemctl",
    "service",
    "mount",
    "umount",
    "fdisk",
    "parted",
    "mkfs",
    "mkswap",
    "swapon",
    "useradd",
    "userdel",
    "usermod",
    "groupadd",
    "groupdel",
    "visudo",
    "iptables",
    "ip6tables",
    "ufw",
    "firewall-cmd",
    "chroot",
    "tcpdump",
    "wpa_cli",
] as const;

const POLKIT_ACTIONS: Record<string, string> = {
    shutdown: "org.freedesktop.login1.power-off",
    halt: "org.freedesktop.login1.halt",
    poweroff: "org.freedesktop.login1.power-off",
};

function hasSensitivePath(args: string[]): boolean {
    return args.some((arg) =>
        SENSITIVE_PATHS.some(
            (path) => arg === path || arg.startsWith(`${path}/`),
        ),
    );
}

function isDangerousRm(args: string[]): boolean {
    const flags = args.filter((a) => a.startsWith("-")).join("");
    const targets = args.filter((a) => !a.startsWith("-"));
    const recursive = flags.includes("r");
    const force = flags.includes("f");

    return (
        recursive &&
        force &&
        targets.some((t) => t === "/" || t === "/*" || t.startsWith("/etc"))
    );
}

function polkitPassword(action: string): PasswordPromptConfig {
    return {
        prelude: [
            `==== AUTHENTICATING FOR ${action} ====`,
            "Authenticating as: guest",
        ],
        prompt: "Password: ",
        maxAttempts: 1,
        retryLine: "",
        failureLines: [
            "polkit-agent-helper-1: pam_authentication_failed: Authentication failure",
        ],
    };
}

/** Returns a block response or null if the input is not a blocked command. */
export function getCommandBlock(input: string): CommandBlock | null {
    const trimmed = input.trim();
    if (!trimmed) return null;

    const parts = trimmed.split(/\s+/);
    const cmd = parts[0]!.toLowerCase();
    const args = parts.slice(1);
    const argsLower = args.map((a) => a.toLowerCase());
    const joined = trimmed.toLowerCase();

    if (cmd === "sudo") {
        return {
            type: "password",
            config: {
                prompt: "[sudo] password for guest: ",
                maxAttempts: 3,
                retryLine: "Sorry, try again.",
                failureLines: ["sudo: 3 incorrect password attempts"],
            },
        };
    }

    if (cmd === "su") {
        return {
            type: "password",
            config: {
                prompt: "Password: ",
                maxAttempts: 1,
                retryLine: "",
                failureLines: ["su: Authentication failure"],
            },
        };
    }

    if (cmd === "passwd") {
        return {
            type: "password",
            config: {
                prelude: ["Changing password for guest."],
                prompt: "(current) UNIX password: ",
                maxAttempts: 1,
                retryLine: "",
                failureLines: [
                    "passwd: Authentication token manipulation error",
                    "passwd: password unchanged",
                ],
            },
        };
    }

    if (cmd === "rm") {
        if (args.length === 0) {
            return {
                type: "lines",
                lines: ["rm: missing operand", "Try 'rm --help' for more information."],
            };
        }

        if (isDangerousRm(argsLower)) {
            return {
                type: "lines",
                lines: [
                    "rm: it is dangerous to operate recursively on '/'",
                    "rm: use --no-preserve-root to override this failsafe",
                ],
            };
        }

        const target = args.find((arg) => !arg.startsWith("-")) ?? args[0]!;
        return {
            type: "lines",
            lines: [`rm: cannot remove '${target}': Permission denied`],
        };
    }

    if (
        (cmd === "chmod" || cmd === "chown" || cmd === "chgrp") &&
        (argsLower.includes("/") || hasSensitivePath(argsLower))
    ) {
        return {
            type: "lines",
            lines: [`${cmd}: changing permissions of '/': Operation not permitted`],
        };
    }

    if (
        ["cat", "less", "more", "head", "tail", "nano", "vim", "vi", "emacs"].includes(
            cmd,
        ) &&
        hasSensitivePath(argsLower)
    ) {
        const target = args.find((a) =>
            SENSITIVE_PATHS.some(
                (p) => a.toLowerCase() === p || a.toLowerCase().startsWith(`${p}/`),
            ),
        );
        return {
            type: "lines",
            lines: [`${cmd}: ${target ?? args[0]}: Permission denied`],
        };
    }

    if (cmd === "dd" && joined.includes("/dev/")) {
        return {
            type: "lines",
            lines: ["dd: failed to open '/dev/sda': Permission denied"],
        };
    }

    if (cmd === "ssh") {
        const lIndex = argsLower.indexOf("-l");
        const loginRoot =
            joined.includes("root@") ||
            (lIndex !== -1 && argsLower[lIndex + 1] === "root");
        if (loginRoot) {
            return { type: "lines", lines: ["Permission denied (publickey)."] };
        }
    }

    if (cmd === "kill" && argsLower.includes("-9") && argsLower.includes("1")) {
        return {
            type: "lines",
            lines: ["bash: kill: (1) - Operation not permitted"],
        };
    }

    if (cmd === "crontab" && (argsLower.includes("-e") || argsLower.includes("-r"))) {
        return {
            type: "lines",
            lines: ["crontabs/guest/: fopen: Permission denied"],
        };
    }

    if (cmd === "init" && (argsLower.includes("0") || argsLower.includes("6"))) {
        return { type: "lines", lines: ["telinit: must be superuser."] };
    }

    if (cmd === "apt" || cmd === "apt-get") {
        return {
            type: "password",
            config: {
                prompt: "[sudo] password for guest: ",
                maxAttempts: 3,
                retryLine: "Sorry, try again.",
                failureLines: ["sudo: 3 incorrect password attempts"],
            },
        };
    }

    if (cmd === "systemctl") {
        return {
            type: "password",
            config: {
                prompt: "[sudo] password for guest: ",
                maxAttempts: 3,
                retryLine: "Sorry, try again.",
                failureLines: ["sudo: 3 incorrect password attempts"],
            },
        };
    }

    if (cmd in POLKIT_ACTIONS) {
        return {
            type: "password",
            config: polkitPassword(POLKIT_ACTIONS[cmd]!),
        };
    }

    if (cmd === "iptables" || cmd === "ip6tables") {
        return { type: "lines", lines: [`${cmd}: Could not open socket.`] };
    }

    if (cmd === "ufw") {
        return {
            type: "lines",
            lines: ["ERROR: You need to be root to run this script"],
        };
    }

    if (["useradd", "userdel", "usermod"].includes(cmd)) {
        return {
            type: "lines",
            lines: [
                `${cmd}: Permission denied.`,
                `${cmd}: cannot lock /etc/passwd; try again later.`,
            ],
        };
    }

    if (cmd === "visudo") {
        return {
            type: "lines",
            lines: ["visudo: /etc/sudoers: Permission denied"],
        };
    }

    if (cmd === "mount" || cmd === "umount") {
        return {
            type: "lines",
            lines: [`${cmd}: only root can ${cmd} on this system`],
        };
    }

    if (NOT_FOUND.includes(cmd as (typeof NOT_FOUND)[number])) {
        return { type: "lines", lines: [`bash: ${cmd}: command not found`] };
    }

    if (ROOT_ONLY.includes(cmd as (typeof ROOT_ONLY)[number])) {
        return { type: "lines", lines: [`${cmd}: Permission denied`] };
    }

    if (["hack", "pwn", "exploit", "rootme", "getroot"].includes(cmd)) {
        return {
            type: "lines",
            lines: [
                `${cmd}: command not found`,
                "Nice try. This incident will be reported.",
            ],
        };
    }

    return null;
}
